import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { serviceBySlug, services } from "@/lib/data/services";
import { cities, primaryCities } from "@/lib/data/cities";
import { sectors as allSectors } from "@/lib/data/sectors";
import { extendedServiceSections } from "@/lib/data/extended-content";
import { withCanadaKeywords } from "@/lib/data/canada-keywords";
import useSEO from "@/hooks/useSEO";
import RevealText from "@/components/RevealText";
import { ArrowUpRight } from "lucide-react";

const HERO_IMAGES = {
  "mining-security":
    "https://images.unsplash.com/photo-1655549136009-6aa6ff5de57e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxtaW5pbmclMjBpbmR1c3RyaWFsJTIwc2l0ZXxlbnwwfHx8fDE3ODUxMDU3MjB8MA&ixlib=rb-4.1.0&q=85",
  "mobile-patrol":
    "https://images.unsplash.com/photo-1680714246510-94d1055e4a8b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHw0fHxzZWN1cml0eSUyMGd1YXJkJTIwcGF0cm9sJTIwbmlnaHR8ZW58MHx8fHwxNzg1MTA1NzIwfDA&ixlib=rb-4.1.0&q=85",
};
const DEFAULT_HERO =
  "https://images.unsplash.com/photo-1517913451214-e22ce660e086?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxzZWN1cml0eSUyMGd1YXJkJTIwcGF0cm9sJTIwbmlnaHR8ZW58MHx8fHwxNzg1MTA1NzIwfDA&ixlib=rb-4.1.0&q=85";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = serviceBySlug(slug);

  // Hooks must run unconditionally; give useSEO safe values when service missing.
  useSEO({
    title: service
      ? `${service.name} — Manitoba Security Services`
      : "Service — Manitoba Security Services",
    description: service ? service.hero : undefined,
    keywords: service ? withCanadaKeywords(service.keywords) : undefined,
    canonical:
      typeof window !== "undefined"
        ? window.location.origin + `/services/${slug}`
        : `/services/${slug}`,
    jsonLd: service
      ? {
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: service.name,
          provider: {
            "@type": "SecurityService",
            name: "Manitoba Security Services",
            areaServed: "Manitoba, Canada",
          },
          areaServed: { "@type": "AdministrativeArea", name: "Manitoba" },
          description: service.hero,
        }
      : undefined,
  });

  if (!service) return <Navigate to="/services" replace />;

  const hero = HERO_IMAGES[service.slug] || DEFAULT_HERO;
  const relatedCities = primaryCities;
  const otherServices = services.filter((s) => s.slug !== service.slug).slice(0, 6);
  const relatedSectors = allSectors.filter((sc) => sc.serviceSlug === service.slug);
  const extended = extendedServiceSections(service);
  const combinedSections = [...service.sections, ...extended];

  return (
    <div className="bg-[#050505] pt-24 md:pt-28">
      {/* Hero */}
      <section className="relative min-h-[85svh] w-full overflow-hidden border-b border-[#1e1e1e]">
        <img
          src={hero}
          alt={`${service.name} — visual reference`}
          className="absolute inset-0 h-full w-full object-cover grayscale contrast-125 opacity-60"
          loading="eager"
        />
        <div className="absolute inset-0 bg-[#050505]/50" />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 65% 50% at 30% 45%, transparent 0%, rgba(5,5,5,0.85) 100%)",
          }}
        />

        <div className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-10 pt-24 md:pt-32 pb-16 grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-2">
            <p className="font-editorial italic text-[#FF3B30] text-sm">File</p>
            <p className="font-editorial italic text-white text-6xl md:text-7xl leading-none">
              {service.number}
            </p>
          </div>
          <div className="col-span-12 md:col-span-10">
            <p className="font-body uppercase tracking-[0.18em] text-[#a0a0a0] text-xs mb-4">
              Service Discipline
            </p>
            <RevealText
              lines={service.name.split(" ")}
              className="font-display font-black uppercase text-white tracking-tightest leading-[0.85] text-[clamp(3rem,10vw,10rem)]"
              stagger={0.1}
              delay={0.1}
            />
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.9 }}
              className="mt-8 font-body text-lg md:text-xl text-white/90 max-w-3xl leading-relaxed"
            >
              {service.hero}
            </motion.p>
          </div>
        </div>
      </section>

      {/* Body — chapters */}
      <section className="mx-auto max-w-[1600px] px-6 md:px-10 py-20 md:py-32 space-y-20 md:space-y-32">
        {combinedSections.map((sec, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="grid grid-cols-12 gap-6 md:gap-10"
          >
            <div className="col-span-12 md:col-span-4">
              <p className="font-editorial italic text-[#FF3B30] text-sm">
                Chapter {String(i + 1).padStart(2, "0")}
              </p>
              <h3 className="mt-4 font-display font-black uppercase tracking-tight text-white text-2xl md:text-3xl leading-tight">
                {sec.h}
              </h3>
            </div>
            <div className="col-span-12 md:col-span-7 md:col-start-6">
              <p className="font-body text-base md:text-lg text-[#a0a0a0] leading-[1.75] max-w-2xl">
                {sec.p}
              </p>
            </div>
          </motion.div>
        ))}
      </section>

      {/* SEO keyword band */}
      <section className="border-y border-[#1e1e1e] bg-[#0b0b0b] py-12">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10">
          <p className="font-editorial italic text-[#FF3B30] text-sm mb-4">
            Directory tags
          </p>
          <ul className="flex flex-wrap gap-x-4 gap-y-2">
            {service.keywords.map((k) => (
              <li
                key={k}
                className="font-body text-xs md:text-sm uppercase tracking-[0.14em] text-[#a0a0a0] border border-[#2a2a2a] px-3 py-1"
              >
                {k}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Sector programs featuring this service */}
      {relatedSectors.length > 0 && (
        <section className="border-t border-[#1e1e1e] bg-[#0b0b0b]">
          <div className="mx-auto max-w-[1600px] px-6 md:px-10 py-20 md:py-24">
            <div className="grid grid-cols-12 gap-6 mb-10">
              <div className="col-span-12 md:col-span-4">
                <p className="font-editorial italic text-[#FF3B30] text-sm">
                  Sector programs
                </p>
              </div>
              <div className="col-span-12 md:col-span-8">
                <h3 className="font-display font-black uppercase tracking-tight text-white text-3xl md:text-5xl leading-tight">
                  {service.name}{" "}
                  <span className="font-editorial italic font-normal text-[#a0a0a0]">
                    in specific
                  </span>{" "}
                  communities
                </h3>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedSectors.map((sc) => (
                <Link
                  key={sc.slug}
                  to={`/sectors/${sc.slug}`}
                  data-testid={`service-sector-${sc.slug}`}
                  className="group border border-[#1e1e1e] hover:border-[#FF3B30] p-6 md:p-8 transition-colors block"
                >
                  <p className="font-editorial italic text-[#FF3B30] text-sm">
                    Sector
                  </p>
                  <h4 className="mt-2 font-display uppercase tracking-tight text-white text-lg md:text-xl leading-tight group-hover:translate-x-1 transition-transform">
                    {sc.heading}
                  </h4>
                  <p className="mt-3 font-editorial italic text-[#a0a0a0] text-sm">
                    {sc.lede}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* City intersection — internal linking for SEO */}
      <section className="mx-auto max-w-[1600px] px-6 md:px-10 py-20 md:py-32">
        <div className="grid grid-cols-12 gap-6 mb-12">
          <div className="col-span-12 md:col-span-4">
            <p className="font-editorial italic text-[#FF3B30] text-sm">
              Coverage
            </p>
          </div>
          <div className="col-span-12 md:col-span-8">
            <h3 className="font-display font-black uppercase tracking-tight text-white text-3xl md:text-5xl leading-tight">
              {service.name}{" "}
              <span className="font-editorial italic font-normal text-[#a0a0a0]">
                across
              </span>{" "}
              Manitoba
            </h3>
          </div>
        </div>
        <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-6 gap-y-3">
          {cities.map((c) => (
            <li key={c.slug}>
              <Link
                to={`/services/${service.slug}/${c.slug}`}
                data-testid={`service-city-link-${c.slug}`}
                className="group flex items-baseline justify-between border-b border-[#1e1e1e] py-3 hover:border-[#FF3B30] transition-colors"
              >
                <span className="font-display uppercase tracking-tight text-white text-sm md:text-base group-hover:translate-x-1 transition-transform">
                  {service.name} · {c.name}
                </span>
                <ArrowUpRight className="text-white/40 group-hover:text-[#FF3B30]" size={14} />
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {/* Related services */}
      <section className="border-t border-[#1e1e1e] bg-[#0b0b0b]">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10 py-20 md:py-24">
          <p className="font-editorial italic text-[#FF3B30] text-sm mb-8">
            Continue reading
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {otherServices.slice(0, 3).map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`related-service-${s.slug}`}
                className="group border border-[#1e1e1e] hover:border-[#FF3B30] p-8 transition-colors block"
              >
                <p className="font-editorial italic text-[#FF3B30] text-sm">
                  {s.number}
                </p>
                <h4 className="mt-2 font-display uppercase tracking-tight text-white text-xl md:text-2xl leading-tight group-hover:translate-x-1 transition-transform">
                  {s.name}
                </h4>
                <p className="mt-3 font-editorial italic text-[#a0a0a0] text-sm">
                  {s.tag}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
